import { BOT_ARCHETYPES, type BotArchetype } from '@goodtime/shared';
import { resolveEntryUrl } from '@goodtime/engine';
import { BotBadge } from './BotBadge';

// Stage 3 install-excision (2026-06-06) — 16 static avatar imports
// (~23MB into the install) replaced with manifest-routed
// {@link resolveEntryUrl} lookups. Archetype id → repo-relative
// folder slug; the {@code idle.png} / {@code lost_prize.png} states
// live under that folder.
const ARCHETYPE_FOLDER: Record<BotArchetype, string> = {
  CALM_CARL: 'calm_carl',
  BRAVE_BEA: 'brave_bea',
  QUIET_QUINN: 'quiet_quinn',
  FRIENDLY_FOX: 'friendly_fox',
  WILD_WILLA: 'wild_willa',
  SHIELDED_SAM: 'shielded_sam',
  LUCKY_LOU: 'lucky_lou',
  STOIC_STAN: 'stoic_stan',
};

export type AvatarVisualState = 'idle' | 'lost_prize';

function avatarUrl(archetype: BotArchetype, state: AvatarVisualState): string {
  const folder = ARCHETYPE_FOLDER[archetype];
  return resolveEntryUrl({ path: `avatar/${folder}/${state}.png` }) ?? '';
}

export interface CastAvatarProps {
  archetype: BotArchetype;
  /** Visual state — controls which PNG variant is rendered. */
  state?: AvatarVisualState;
  /** Diameter in px. Cast strip uses 64; profile card uses 96; wheel
   * perimeter uses 72. */
  size?: number;
  /** Render the per-archetype accent ring around the avatar (used when
   * the bot just acted, or to highlight the player's own seat). */
  active?: boolean;
  /** Show the bot badge overlay. Default true — every bot avatar has
   * the badge per Bot Spec §7.4. */
  showBadge?: boolean;
  /** Optional click — opens the {@code ProfileCard} modal. */
  onClick?: () => void;
}

/**
 * One bot avatar from the cast. Renders the commission-grade PNG
 * with the bot badge overlay, an optional per-archetype accent ring,
 * and a clickable affordance for opening the profile card.
 *
 * <p>All sizing happens via CSS so a single 256×256 source PNG scales
 * down to 32px (mini cast indicator), 64px (cast strip), 96px
 * (profile card hero), or up to 256px (winner callout) without
 * additional assets.
 */
export function CastAvatar({
  archetype,
  state = 'idle',
  size = 64,
  active = false,
  showBadge = true,
  onClick,
}: CastAvatarProps) {
  const meta = BOT_ARCHETYPES[archetype];
  const url = avatarUrl(archetype, state);
  const ringWidth = active ? Math.max(2, Math.round(size * 0.045)) : 0;

  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      onClick={onClick}
      type={onClick ? ('button' as const) : undefined}
      title={meta.displayName}
      style={{
        position: 'relative',
        width: size,
        height: size,
        borderRadius: '50%',
        padding: 0,
        border: 'none',
        background: active ? meta.accent : 'transparent',
        cursor: onClick ? 'pointer' : 'default',
        // CSS box-shadow ring is cheaper than a wrapping div + lets the
        // accent colour bleed through the corners of the masked image.
        boxShadow: active
          ? `0 0 0 ${ringWidth}px ${meta.accent}, 0 4px 12px rgba(0, 0, 0, 0.35)`
          : '0 2px 8px rgba(0, 0, 0, 0.3)',
        overflow: 'visible',
        flexShrink: 0,
      }}
    >
      <img
        src={url}
        alt={meta.displayName}
        draggable={false}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          display: 'block',
          background: '#0B1838',
          objectFit: 'cover',
        }}
      />
      {showBadge ? <BotBadge size={Math.max(10, Math.round(size * 0.22))} /> : null}
    </Tag>
  );
}
